import { Link, router } from '@inertiajs/react'
import { useState, useRef, useCallback, useEffect } from 'react'
import { formatLempiras } from '../../lib/format'
import { useCart, CartProvider, Cart } from '../../lib/cart'
import CartHeader from '../../lib/CartHeader'
import { PlusIcon, ArrowIcon } from '../../lib/icons'

interface Account { id: number; name: string; slug: string; logo_url?: string | null; carousel_images?: string[] }
interface Tag { id: number; name: string }
interface TagFacet { id: number; name: string; count: number }
interface Product {
  id: number; name: string; price_cents: number; price_with_tax_cents: number; category_id?: number
  category_name?: string; image_url?: string; images?: { id: string; url: string; thumb_url: string; filename: string }[]
  has_variants: boolean
  track_inventory?: boolean; stock_quantity?: number | null
  tags?: Tag[]
}
interface Category { id: number; name: string }
interface Pagination { page: number; per_page: number; total: number; total_pages: number }
interface PendingPayment { reference_number: string; total_cents: number; state: string; created_at: string }
interface Props { account: Account; products: Product[]; categories: Category[]; tags?: TagFacet[]; selected_tag_ids?: number[]; cart?: Cart; search?: string; pagination?: Pagination; pending_payment?: PendingPayment | null }

/* ---- main export: wraps content with CartProvider ---- */
export default function CatalogIndex(props: Props) {
  const { account, cart } = props
  return (
    <CartProvider cart={cart} slug={account.slug}>
      <CatalogIndexInner {...props} />
    </CartProvider>
  )
}

/* ---- inner component: has useCart() INSIDE CartProvider ---- */
function CatalogIndexInner({ account, products, categories, tags, selected_tag_ids, search, pagination, pending_payment }: { account: Account; products: Product[]; categories: Category[]; tags?: TagFacet[]; selected_tag_ids?: number[]; search?: string; pagination?: Pagination; pending_payment?: PendingPayment | null }) {
  const [s, setS] = useState(search || '')
  const { addItem } = useCart()

  const currentCategory = categories.find(c => c.id === Number(new URLSearchParams(window.location.search).get('category_id')))
  const selectedTags = selected_tag_ids || []

  const fetchWith = (params: Record<string, unknown>) => {
    router.get(`/${account.slug}/catalog`, params as any, { preserveState: true, replace: true })
  }

  const handleSearch = (v: string) => {
    setS(v)
    // ⚠️ reenviar TODOS los filtros activos (search no debe perder categoría/tags)
    fetchWith({ search: v, category_id: currentCategory?.id, tag_ids: selectedTags.length ? selectedTags : undefined, page: 1 })
  }

  const toggleTag = (id: number) => {
    const next = selectedTags.includes(id) ? selectedTags.filter(t => t !== id) : [...selectedTags, id]
    fetchWith({ search: s || undefined, category_id: currentCategory?.id, tag_ids: next.length ? next : undefined, page: 1 })
  }

  const showGrouped = !currentCategory
  const grouped: { category: Category | null; products: Product[] }[] = showGrouped
    ? categories.map(cat => ({ category: cat, products: products.filter(p => p.category_id === cat.id) })).filter(g => g.products.length > 0)
    : [{ category: null, products }]
  if (showGrouped) {
    const uncategorized = products.filter(p => !p.category_id)
    if (uncategorized.length > 0) grouped.push({ category: { id: 0, name: 'Otros' }, products: uncategorized })
  }

  const categoryParam = new URLSearchParams(window.location.search).get('category_id') || undefined

  return (
    <div className="min-h-screen bg-gray-50">
      <CartHeader slug={account.slug} accountName={account.name} logoUrl={account.logo_url} />
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Large centered logo — first thing customers see */}
        {account.logo_url && (
          <div className="flex justify-center mb-4">
            <img src={account.logo_url} alt={account.name} className="h-32 md:h-44 object-contain" />
          </div>
        )}

        {/* Panel de pago pendiente — lo primero que el cliente ve bajo el logo */}
        {pending_payment && pending_payment.state === 'awaiting_payment' && (
          <div className="mb-4 rounded-xl border-2 border-amber-400 bg-amber-50 p-4 md:p-5 shadow-md">
            <div className="flex items-start gap-3">
              <span className="text-3xl">🧾</span>
              <div className="flex-1 min-w-0">
                <p className="text-base md:text-lg font-bold text-[#202020]">¡Tienes un pago pendiente!</p>
                <p className="text-sm text-gray-700 mt-1">
                  Pedido <span className="font-semibold">{pending_payment.reference_number}</span> por{' '}
                  <span className="font-semibold">{formatLempiras(pending_payment.total_cents)}</span>.
                  Sube tu comprobante de pago para confirmar tu pedido.
                </p>
                <div className="flex flex-wrap gap-2 mt-3">
                  <Link
                    href={`/${account.slug}/catalog/upload-proof?reference_number=${pending_payment.reference_number}`}
                    className="flex-1 sm:flex-none text-center bg-[#55ACEE] text-white px-4 py-2.5 rounded-lg text-sm font-semibold hover:bg-[#3b9ed6] transition-colors"
                  >
                    📤 Subir comprobante
                  </Link>
                  <button
                    onClick={() => {
                      if (confirm(`¿Cancelar el pedido ${pending_payment.reference_number}?`)) {
                        router.post(`/${account.slug}/catalog/orders/${pending_payment.reference_number}/cancel`)
                      }
                    }}
                    className="flex-1 sm:flex-none text-center border border-gray-300 bg-white text-gray-700 px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-gray-100 transition-colors"
                  >
                    Cancelar pedido
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        {pending_payment && pending_payment.state === 'confirming' && (
          <div className="mb-4 rounded-xl border border-green-300 bg-green-50 p-4 md:p-5">
            <div className="flex items-center gap-3">
              <span className="text-2xl">✅</span>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-[#202020]">Comprobante en revisión</p>
                <p className="text-sm text-gray-600">
                  Pedido <span className="font-semibold">{pending_payment.reference_number}</span>. Estamos
                  verificando tu pago, en breve te confirmamos.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Carousel — slides through extra images after the logo */}
        {account.carousel_images && account.carousel_images.length > 0 && (
          <Carousel images={account.carousel_images} />
        )}

        <div className="bg-white rounded-lg shadow p-4 mb-4">
          <input type="text" placeholder="Buscar productos..." value={s}
            onChange={(e) => handleSearch(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#55ACEE]" />
        </div>
        {categories.length > 0 && (
          <div className="mb-6">
            <p className="text-sm font-medium text-gray-500 mb-2">Categorías</p>
            <div className="flex flex-wrap gap-2">
            <Link href={`/${account.slug}/catalog`}
              className={`px-3 py-1.5 rounded text-sm ${!currentCategory ? 'bg-[#55ACEE] text-white' : 'bg-white text-gray-700'}`}>Todas</Link>
            {categories.map((c) => (
              <Link key={c.id} href={`/${account.slug}/catalog?category_id=${c.id}`}
                className={`px-3 py-1.5 rounded text-sm ${currentCategory?.id === c.id ? 'bg-[#55ACEE] text-white' : 'bg-white text-gray-700 hover:bg-gray-100'}`}>{c.name}</Link>
            ))}
          </div>
          </div>
        )}
        {/* Tags con checkboxes — conteo facet: solo tags con resultados */}
        {tags && tags.length > 0 && (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-gray-500">Filtrar por tags</p>
              {selectedTags.length > 0 && (
                <button onClick={() => fetchWith({ search: s || undefined, category_id: currentCategory?.id, page: 1 })}
                  className="text-xs text-[#55ACEE] hover:underline">Limpiar tags ({selectedTags.length})</button>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {tags.map(t => {
                const active = selectedTags.includes(t.id)
                return (
                  <button key={t.id} onClick={() => toggleTag(t.id)}
                    className={`px-3 py-1.5 rounded text-sm border transition-colors ${
                      active
                        ? 'bg-[#FFC107] border-[#FFC107] text-[#202020] font-semibold'
                        : 'bg-white border-gray-300 text-gray-700 hover:border-[#FFC107]'
                    }`}>
                    <input type="checkbox" checked={active} onChange={() => toggleTag(t.id)} className="mr-1.5 w-3.5 h-3.5 accent-[#202020] align-middle" onClick={e => e.stopPropagation()} />
                    {t.name}
                    <span className={`ml-1.5 text-xs ${active ? 'text-[#202020]/60' : 'text-gray-400'}`}>({t.count})</span>
                  </button>
                )
              })}
            </div>
            {selectedTags.length > 0 && (
              <p className="text-xs text-gray-400 mt-2">Los tags muestran solo los que tienen productos además de tu selección.</p>
            )}
          </div>
        )}
        {products.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-12 text-center"><p className="text-gray-500 text-lg">No hay productos disponibles</p></div>
        ) : (
          <>
            <div className="space-y-8">
              {grouped.map((group) => (
                <section key={group.category?.id ?? 'all'}>
                  {group.category && (
                    <h2 className="text-xl font-bold text-[#202020] mb-3 border-b border-gray-200 pb-2">
                      {group.category.name}<span className="text-sm text-gray-500 font-normal ml-2">({group.products.length})</span>
                    </h2>
                  )}
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {group.products.map((p) => (
                      <ProductCard key={p.id} product={p} account={account} onAdd={addItem} />
                    ))}
                  </div>
                </section>
              ))}
            </div>
            {pagination && pagination.total_pages > 1 && (
              <div className="mt-8 flex items-center justify-center gap-4">
                <button
                  onClick={() => router.get(`/${account.slug}/catalog`, { search: s, category_id: categoryParam, tag_ids: selectedTags.length ? selectedTags : undefined, page: pagination.page - 1 } as any, { preserveState: true, replace: true })}
                  disabled={pagination.page <= 1}
                  className="px-4 py-2 text-sm rounded bg-white border border-gray-300 text-[#202020] hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  ← Anterior
                </button>
                <span className="text-sm text-gray-600">
                  Página {pagination.page} de {pagination.total_pages}
                </span>
                <button
                  onClick={() => router.get(`/${account.slug}/catalog`, { search: s, category_id: categoryParam, tag_ids: selectedTags.length ? selectedTags : undefined, page: pagination.page + 1 } as any, { preserveState: true, replace: true })}
                  disabled={pagination.page >= pagination.total_pages}
                  className="px-4 py-2 text-sm rounded bg-white border border-gray-300 text-[#202020] hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Siguiente →
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

function ProductCard({ product, account, onAdd }: { product: Product; account: Account; onAdd: (pid: number, o?: { variantId?: number | null; quantity?: number }) => void }) {
  const hasVariants = product.has_variants
  const { items } = useCart()
  const tracksStock = product.track_inventory === true
  const maxStock = tracksStock ? Number(product.stock_quantity ?? 0) : null
  const alreadyInCart = items
    .filter(i => i.product_id === product.id)
    .reduce((s, i) => s + i.quantity, 0)
  const canAddMore = maxStock === null || alreadyInCart < maxStock
  const outOfStock = tracksStock && !hasVariants && maxStock !== null && maxStock <= 0
  const images = product.images || []
  const hasMultipleImages = images.length > 1
  const scrollRef = useRef<HTMLDivElement>(null)
  const [scrollIdx, setScrollIdx] = useState(0)

  const scrollTo = useCallback((index: number) => {
    const el = scrollRef.current
    if (!el) return
    const children = el.children
    if (index >= 0 && index < children.length) {
      children[index].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' })
      setScrollIdx(index)
    }
  }, [])

  // Update scroll index on manual scroll
  const handleScroll = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    const width = el.clientWidth
    const idx = Math.round(el.scrollLeft / width)
    if (idx !== scrollIdx) setScrollIdx(idx)
  }, [scrollIdx])

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden hover:shadow-lg transition-shadow flex flex-col">
      <Link href={`/${account.slug}/catalog/${product.id}`} className="block">
        {images.length > 0 ? (
          <div className="w-full aspect-square bg-gray-100 overflow-hidden relative">
            {/* Scrollable image strip */}
            {hasMultipleImages ? (
              <>
                <div
                  ref={scrollRef}
                  onScroll={handleScroll}
                  className="w-full h-full flex overflow-x-auto snap-x snap-mandatory scrollbar-hide"
                >
                  {images.map((img, i) => (
                    <div key={img.id || i} className="flex-shrink-0 w-full h-full snap-center">
                      <img src={img.url} alt={`${product.name} - ${i + 1}`} className="w-full h-full object-contain" loading="lazy" />
                    </div>
                  ))}
                </div>
                {/* Arrow buttons */}
                <button
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); scrollTo(scrollIdx - 1) }}
                  disabled={scrollIdx <= 0}
                  className="absolute left-1 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-white/70 hover:bg-white shadow flex items-center justify-center text-[#202020] text-sm disabled:hidden transition-all"
                  aria-label="Imagen anterior"
                >‹</button>
                <button
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); scrollTo(scrollIdx + 1) }}
                  disabled={scrollIdx >= images.length - 1}
                  className="absolute right-1 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-white/70 hover:bg-white shadow flex items-center justify-center text-[#202020] text-sm disabled:hidden transition-all"
                  aria-label="Imagen siguiente"
                >›</button>
                {/* Dot indicators */}
                <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
                  {images.slice(0, 5).map((_, i) => (
                    <button
                      key={i}
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); scrollTo(i) }}
                      className={`w-1.5 h-1.5 rounded-full transition-colors ${i === scrollIdx ? 'bg-[#55ACEE]' : 'bg-gray-300 hover:bg-gray-400'}`}
                      aria-label={`Ir a imagen ${i + 1}`}
                    />
                  ))}
                  {images.length > 5 && <span className="text-[10px] text-gray-400 leading-none">+{images.length - 5}</span>}
                </div>
              </>
            ) : (
              <img src={images[0].url} alt={product.name} className="w-full h-full object-contain" loading="lazy" />
            )}
          </div>
        ) : (
          <div className="w-full aspect-square bg-gray-200 flex items-center justify-center text-gray-400 text-4xl">📦</div>
        )}
      </Link>
      <div className="p-3 flex-1 flex flex-col">
        {product.category_name && <p className="text-xs text-gray-500">{product.category_name}</p>}
        {product.tags && product.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-1">
            {product.tags.slice(0, 3).map(t => (
              <span key={t.id} className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-[#FFC107]/15 text-[#7a6a1a] border border-[#FFC107]/30">{t.name}</span>
            ))}
            {product.tags.length > 3 && <span className="text-[10px] text-gray-400 self-center">+{product.tags.length - 3}</span>}
          </div>
        )}
        <Link href={`/${account.slug}/catalog/${product.id}`} className="flex-1">
          <p className="font-semibold text-[#202020] line-clamp-2 mb-1 hover:text-[#55ACEE]">{product.name}</p>
        </Link>
        <div className="flex items-center justify-between mt-1">
          <div>
            <p className="text-[#55ACEE] font-bold text-lg">{formatLempiras(product.price_with_tax_cents)}</p>
            <span className="text-xs text-gray-400">ISV incluido</span>
            {tracksStock && !hasVariants && (
              <p className={`text-xs ${outOfStock || !canAddMore ? 'text-red-500' : 'text-gray-500'}`}>
                {outOfStock ? 'Sin stock' : `Stock: ${maxStock}`}
              </p>
            )}
          </div>
          {hasVariants ? (
            <Link href={`/${account.slug}/catalog/${product.id}`}
              className="bg-[#FFC107] text-white w-10 h-10 rounded-full flex items-center justify-center hover:bg-[#e6ad06] active:scale-95 transition-transform shadow"
              aria-label="Ver variantes" title="Ver variantes"><ArrowIcon size={20} /></Link>
          ) : (
            <button
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                if (!canAddMore || outOfStock) return
                onAdd(product.id, { quantity: 1 })
              }}
              disabled={!canAddMore || outOfStock}
              className={`w-10 h-10 rounded-full flex items-center justify-center shadow transition-transform ${
                !canAddMore || outOfStock
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-[#FFC107] text-white hover:bg-[#e6ad06] active:scale-95'
              }`}
              aria-label={!canAddMore || outOfStock ? 'Sin stock disponible' : 'Agregar al carrito'}
              title={!canAddMore || outOfStock ? 'Sin stock disponible' : 'Agregar al carrito'}
            >
              <PlusIcon size={20} />
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

/* ---- Carousel component — auto-rotating image slider ---- */
function Carousel({ images }: { images: string[] }) {
  const [current, setCurrent] = useState(0)
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const startTimer = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current)
    timerRef.current = setInterval(() => {
      setCurrent(prev => (prev + 1) % images.length)
    }, 4000)
  }, [images.length])

  useEffect(() => {
    startTimer()
    return () => { if (timerRef.current) clearInterval(timerRef.current) }
  }, [startTimer])

  if (images.length === 0) return null

  return (
    <div className="mb-4">
      <div className="relative w-full aspect-[3/1] rounded-lg overflow-hidden bg-gray-100">
        {images.map((url, i) => (
          <img
            key={i}
            src={url}
            alt=""
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${i === current ? 'opacity-100' : 'opacity-0'}`}
          />
        ))}
      </div>
      {images.length > 1 && (
        <div className="flex justify-center gap-1.5 mt-2">
          {images.map((_, i) => (
            <button
              key={i}
              onClick={() => { setCurrent(i); startTimer() }}
              className={`w-2 h-2 rounded-full transition-colors ${i === current ? 'bg-[#55ACEE]' : 'bg-gray-300'}`}
            />
          ))}
        </div>
      )}
    </div>
  )
}
